import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAccounts } from '../contexts/AccountContext';
import {
  listHighlightCollections,
  createHighlightCollection,
  deleteHighlightCollection,
  addHighlightCollectionStory,
  removeHighlightCollectionStory,
  listFlows,
  getFlow,
  createFlow,
  updateFlow,
  deleteFlow,
  type HighlightCollection,
  type FlowSummary,
} from '../lib/sections-api';
import ResponseConfigForm, { type ResponseConfig } from '../components/ResponseConfigForm';

const EMPTY_CONFIG: ResponseConfig = { replyText: '', steps: [], buttons: [] };

export default function HighlightsPage() {
  useDocumentTitle('هایلایت‌ها');
  const { selectedAccountId } = useAccounts();
  const [collections, setCollections] = useState<HighlightCollection[]>([]);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState<string | null>(null);
  const [config, setConfig] = useState<ResponseConfig>(EMPTY_CONFIG);
  const [saving, setSaving] = useState(false);
  const [addStoryId, setAddStoryId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (selectedAccountId === null) return;
    let cancelled = false;
    Promise.all([
      listHighlightCollections(selectedAccountId),
      listFlows('highlight', selectedAccountId),
    ])
      .then(([cols, fls]) => {
        if (cancelled) return;
        setCollections(cols);
        setFlows(fls);
      })
      .catch(() => {
        if (!cancelled) {
          setCollections([]);
          setFlows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, reloadKey]);

  const reload = (): void => setReloadKey((k) => k + 1);

  const handleCreate = async (): Promise<void> => {
    if (selectedAccountId === null || newName.trim() === '') return;
    setCreating(true);
    try {
      await createHighlightCollection(selectedAccountId, newName.trim(), []);
      setNewName('');
      reload();
    } catch {
      setError('ایجاد هایلایت ممکن نشد.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCollection = async (id: string): Promise<void> => {
    try {
      await deleteHighlightCollection(id);
      reload();
    } catch {
      // ignore
    }
  };

  const handleAddStory = async (collectionId: string): Promise<void> => {
    if (addStoryId.trim() === '') return;
    try {
      await addHighlightCollectionStory(collectionId, addStoryId.trim());
      setAddStoryId('');
      reload();
    } catch {
      // ignore
    }
  };

  const handleRemoveStory = async (collectionId: string, mediaId: string): Promise<void> => {
    try {
      await removeHighlightCollectionStory(collectionId, mediaId);
      reload();
    } catch {
      // ignore
    }
  };

  const openFlowConfig = async (collectionId: string): Promise<void> => {
    setEditingCollectionId(collectionId);
    const existing = flows.find((f) => f.target_media_id === collectionId);
    if (existing !== undefined) {
      try {
        const detail = await getFlow('highlight', existing.id);
        setConfig({
          replyText: detail.flow.reply_text ?? '',
          steps: detail.steps.map((s) => ({
            kind: s.kind,
            text: s.text,
            media_asset_id: s.media_asset_id,
          })),
          buttons: detail.buttons
            .filter((b) => b.parent_button_id === null)
            .map((b) => ({
              label: b.label,
              action_kind: b.action_kind,
              text: b.text,
              media_asset_id: b.media_asset_id,
              url: b.url,
            })),
        });
      } catch {
        setConfig(EMPTY_CONFIG);
      }
    } else {
      setConfig(EMPTY_CONFIG);
    }
    setConfigModalOpen(true);
  };

  const handleSaveFlow = async (): Promise<void> => {
    if (selectedAccountId === null || editingCollectionId === null) return;
    setSaving(true);
    const payload = {
      instagram_account_id: selectedAccountId,
      target_media_id: editingCollectionId,
      reply_text: config.replyText.trim() === '' ? null : config.replyText,
      steps: config.steps,
      buttons: config.buttons,
    };
    try {
      const existing = flows.find((f) => f.target_media_id === editingCollectionId);
      if (existing !== undefined) {
        await updateFlow('highlight', existing.id, payload);
      } else {
        await createFlow('highlight', payload);
      }
      setConfigModalOpen(false);
      reload();
    } catch {
      setError('ذخیره پیکربندی ممکن نشد.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFlow = async (collectionId: string): Promise<void> => {
    const flow = flows.find((f) => f.target_media_id === collectionId);
    if (flow === undefined) return;
    try {
      await deleteFlow('highlight', flow.id);
      reload();
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <PageHeader
        title="هایلایت‌ها"
        description="پاسخ خودکار برای استوری‌های موجود در هر هایلایت"
      />

      {error !== null && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          <Icon name="alert" className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {selectedAccountId === null ? (
        <EmptyState
          icon="highlight"
          title="حسابی انتخاب نشده"
          description="از بالای صفحه یک حساب انتخاب کنید."
        />
      ) : (
        <>
          <Card className="mb-6">
            <CardBody>
              <p className="mb-1 text-xs font-medium text-slate-700">هایلایت جدید</p>
              <p className="mb-3 text-[11px] text-slate-400">
                API رسمی اینستاگرام قابلیت فهرست هایلایت‌ها را ندارد؛ بنابراین هر هایلایت را دستی
                ثبت کنید و شناسه استوری‌های آن را اضافه کنید.
              </p>
              <div className="flex gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="نام هایلایت (مثلاً «محصولات»)"
                  className="max-w-xs"
                />
                <Button
                  onClick={() => void handleCreate()}
                  loading={creating}
                  disabled={newName.trim() === ''}
                >
                  <Icon name="plus" className="h-4 w-4" />
                  ایجاد
                </Button>
              </div>
            </CardBody>
          </Card>

          {loading ? (
            <Spinner className="py-8" />
          ) : collections.length === 0 ? (
            <EmptyState
              icon="highlight"
              title="هایلایتی ثبت نشده"
              description="اول یک هایلایت ایجاد کنید و سپس استوری‌های آن را اضافه کنید."
            />
          ) : (
            <div className="space-y-4">
              {collections.map((col) => {
                const flow = flows.find((f) => f.target_media_id === col.id);
                return (
                  <Card key={col.id}>
                    <CardHeader
                      title={col.name}
                      description={`${col.story_media_ids.length} استوری`}
                      action={
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => void openFlowConfig(col.id)}
                          >
                            {flow !== undefined ? 'ویرایش پاسخ' : 'پیکربندی پاسخ'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void handleDeleteCollection(col.id)}
                          >
                            <Icon name="trash" className="h-4 w-4 text-rose-500" />
                          </Button>
                        </div>
                      }
                    />
                    <CardBody>
                      {flow !== undefined && (
                        <p className="mb-3 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-700">
                          پاسخ خودکار فعال: {flow.reply_text ?? 'بدون متن'}
                        </p>
                      )}
                      <div className="mb-3 flex gap-2">
                        <Input
                          value={addStoryId}
                          onChange={(e) => setAddStoryId(e.target.value)}
                          placeholder="شناسه استوری (media id)"
                          dir="ltr"
                          className="max-w-xs"
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void handleAddStory(col.id)}
                          disabled={addStoryId.trim() === ''}
                        >
                          افزودن استوری
                        </Button>
                      </div>
                      {col.story_media_ids.length === 0 ? (
                        <p className="text-xs text-slate-400">استوری‌ای اضافه نشده.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {col.story_media_ids.map((mediaId) => (
                            <span
                              key={mediaId}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-600"
                              dir="ltr"
                            >
                              {mediaId}
                              <button
                                type="button"
                                onClick={() => void handleRemoveStory(col.id, mediaId)}
                                className="text-slate-400 hover:text-rose-500"
                                aria-label="حذف"
                              >
                                <Icon name="close" className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {flow !== undefined && (
                        <div className="mt-3 border-t border-slate-100 pt-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void handleDeleteFlow(col.id)}
                          >
                            حذف پیکربندی پاسخ
                          </Button>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <Modal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        title="پیکربندی پاسخ هایلایت"
        wide
      >
        <ResponseConfigForm value={config} onChange={setConfig} />
        <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="secondary" onClick={() => setConfigModalOpen(false)}>
            انصراف
          </Button>
          <Button onClick={() => void handleSaveFlow()} loading={saving}>
            ذخیره
          </Button>
        </div>
      </Modal>
    </div>
  );
}
